import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { DeleteRequest, GetRequest, PostRequest } from "../../services/ApiClientService"
import type {
  ISendWhatsAppMessagePayload,
  IWhatsAppAgentOverviewDto,
  IWhatsAppAgentInstallerInfo,
  IWhatsAppPairingCodeResult,
} from "../../Objects/WhatsAppAutomation/WhatsAppAgentOverview"

type ApiWrappedResponse<T> = {
  success?: boolean
  message?: string
  data?: T
}

export const useWhatsAppAutomation = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const resolveErrorMessage = (message?: string) => {
    const trimmed = message?.trim()
    return trimmed && trimmed.length > 0 ? trimmed : t("label_unknown_error_description")
  }

  function unwrapResponseData<T>(response: unknown, fallbackValue: T): T {
    const wrapped = response as ApiWrappedResponse<T>

    if (typeof response === "object" && response !== null && "success" in response) {
      if (wrapped.success === false) {
        throw new Error(resolveErrorMessage(wrapped.message))
      }

      return (wrapped.data ?? fallbackValue) as T
    }

    if (response === null || response === undefined) {
      return fallbackValue
    }

    return response as T
  }

  const getOverview = async (silent = false): Promise<IWhatsAppAgentOverviewDto[]> => {
    if (!silent) {
      enableLoad()
    }

    try {
      const response = await GetRequest<IWhatsAppAgentOverviewDto[]>(API_ROUTES.WHATSAPP_AUTOMATION.OVERVIEW)

      return unwrapResponseData<IWhatsAppAgentOverviewDto[]>(response, [])
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")

      if (!silent) {
        setError(t("label_title_erro"), message)
      }

      return []
    } finally {
      if (!silent) {
        disableLoad()
      }
    }
  }

  const getInstallerInfo = async (): Promise<IWhatsAppAgentInstallerInfo | null> => {
    try {
      const response = await GetRequest<IWhatsAppAgentInstallerInfo>(API_ROUTES.WHATSAPP_AUTOMATION.INSTALLER)

      return unwrapResponseData<IWhatsAppAgentInstallerInfo | null>(response, null)
    } catch {
      return null
    }
  }

  const generatePairingCode = async (agentName: string): Promise<IWhatsAppPairingCodeResult> => {
    enableLoad()

    try {
      const response = await PostRequest<IWhatsAppPairingCodeResult>(
        API_ROUTES.WHATSAPP_AUTOMATION.GENERATE_PAIRING_CODE,
        {
          agentName,
        },
      )

      return unwrapResponseData<IWhatsAppPairingCodeResult>(response, { agentId: 0, pairingCode: "", expiresAt: "" })
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)
      throw e
    } finally {
      disableLoad()
    }
  }

  const sendMessage = async (payload: ISendWhatsAppMessagePayload) => {
    enableLoad()

    try {
      await PostRequest<object>(API_ROUTES.WHATSAPP_AUTOMATION.SEND_MESSAGE, payload)
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)
      throw e
    } finally {
      disableLoad()
    }
  }

  const deleteAgent = async (agentId: number) => {
    enableLoad()

    try {
      await DeleteRequest<object>(API_ROUTES.WHATSAPP_AUTOMATION.DELETE_AGENT(agentId))
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      throw e
    } finally {
      disableLoad()
    }
  }

  return { getOverview, getInstallerInfo, generatePairingCode, sendMessage, deleteAgent }
}
